import { useState } from 'react';
import {
    Button, Checkbox, Input, Label, ListBox, ListBoxItem, Modal, Select, TextArea, TextField
} from '@heroui/react';

import AvatarStack from '../../common/AvatarStack';
import UserBadge from '../../common/UserBadge';
import CollectionActivity from '../activity/CollectionActivity';
import CommentBubble from '../../common/CommentBubble';
import MentionTextArea from '../../common/MentionTextArea';
import ViewTab from '../../common/ViewTab';
import { DueBadge } from './TaskBoard';
import Notice from '../../common/Notice';
import { TaskStatusChip, TASK_STATUS_APPEARANCE } from '../../common/StatusChip';
import tasksService from '../../../services/tasks.service';
import { mayProgress } from '../../../services/taskAccess';
import { MENTIONS, TASKS, mentionMessageOf, messageOf, SELECT_PLACEHOLDER } from '../../../i18n/el';

// Ο διάλογος ενός καθηκόντος: δημιουργία, επεξεργασία, μεταβολή κατάστασης και
// σημειώσεις προόδου (ΛΑ-18 έως ΛΑ-20).
//
// **Ένας διάλογος και όχι τρεις**, γιατί ο Πίνακας 1 δίνει στους δύο ρόλους
// **διαφορετικά σκέλη του ίδιου αντικειμένου**: ο συντονιστής μεταβάλλει τα πάντα,
// ο ανάδοχος την κατάσταση και τις σημειώσεις. Τρεις οθόνες θα ζητούσαν από τον
// χρήστη να ξέρει ποια του ανήκει πριν την ανοίξει.
//
// **Ο διάλογος αποδίδεται πάντοτε υπό συνθήκη από τον γονέα** (`{isOpen && …}`):
// το κλείσιμο έρχεται και από αποθήκευση ή διαγραφή, δηλαδή από πηγή εκτός του.

const isoDay = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');

// Ο κανόνας του Πίνακα 1 —«ενημέρωση καθηκόντων **που του έχουν ανατεθεί**»—
// ζούσε εδώ ως τοπική συνάρτηση. Μετακόμισε στο `services/taskAccess.js` όταν
// απέκτησε **δεύτερο** καταναλωτή, τον πίνακα Kanban: ο ίδιος κανόνας γραμμένος
// δύο φορές αποκλίνει, και εδώ η απόκλιση θα έδειχνε στοιχείο ελέγχου που ο
// εξυπηρετητής θα απέρριπτε.

export default function TaskDialog({
    task = null,
    collectionId,
    collection,
    resources = [],
    canManage = false,
    currentUserId,
    onClose,
    onSaved
}) {
    const isNew = task === null;
    const canProgress = mayProgress(task, { userId: currentUserId, canManage });

    const [form, setForm] = useState({
        title: task?.title ?? '',
        description: task?.description ?? '',
        resource: task?.resource?.id ?? task?.resource ?? '',
        assignees: (task?.assignees ?? []).map((user) => String(user?.id ?? user)),
        status: task?.status ?? 'todo',
        dueDate: isoDay(task?.dueDate)
    });
    const [note, setNote] = useState('');
    // Ποια από τις δύο δεξιές καρτέλες φαίνεται. **Τοπική κατάσταση και όχι
    // παράμετρος διεύθυνσης**, σε αντίθεση με τις καρτέλες της σελίδας συλλογής
    // (D27): εκείνες πρέπει να είναι κοινοποιήσιμες, γιατί ο σύνδεσμος οδηγεί σε
    // **οθόνη**· εδώ ο διάλογος έχει ήδη τον δικό του μόνιμο σύνδεσμο και η
    // εσωτερική του καρτέλα δεν είναι προορισμός.
    const [pane, setPane] = useState('notes');
    const [error, setError] = useState('');
    const [isSaving, setSaving] = useState(false);
    const [isConfirmingDelete, setConfirmingDelete] = useState(false);

    const set = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));
    const members = (collection?.members ?? []).map((member) => member.user).filter(Boolean);
    const notes = task?.notes ?? [];

    const report = (apiError) => {
        setSaving(false);
        // D44: **η ίδια** μετάφραση με τα υπόλοιπα πεδία συνομιλίας. Όταν η άρνηση
        // αφορά αναφορές, το μήνυμα **ονομάζει ποια** — αλλιώς ο χρήστης βλέπει
        // «ελέγξτε τα στοιχεία» για ένα όνομα μέσα σε κείμενο τριών γραμμών, και
        // η μόνη ενέργεια που του απομένει είναι να τα σβήσει όλα.
        setError(mentionMessageOf(apiError));
    };

    // Ονομασμένη συνάρτηση και όχι ενσωματωμένο βέλος: ο στατικός έλεγχος
    // καλωδίωσης διαβάζει τα χαρακτηριστικά JSX ως κείμενο μέχρι το πρώτο `>`,
    // και το βέλος περιέχει ένα.
    function handleStatusChange(key) {
        set('status')(String(key));
    }

    function toggleAssignee(userId) {
        setForm((current) => ({
            ...current,
            assignees: current.assignees.includes(userId)
                ? current.assignees.filter((id) => id !== userId)
                : [...current.assignees, userId]
        }));
    }

    /** Τα πεδία διαχείρισης, στη μορφή που δέχεται το σχήμα `.strict()` του API. */
    const managedFields = () => ({
        title: form.title.trim(),
        description: form.description.trim(),
        resource: form.resource || null,
        assignees: form.assignees,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null
    });

    function save(event) {
        event.preventDefault();
        setError('');
        setSaving(true);

        if (isNew) {
            tasksService
                .createTask(collectionId, { ...managedFields(), status: form.status })
                .then(onSaved, report);
            return;
        }

        // **Μόνο τα πεδία που άλλαξαν, χωρισμένα κατά δικαίωμα.** Ο ανάδοχος που δεν
        // είναι συντονιστής στέλνει **μόνο** την κατάσταση: αν το αίτημα περιείχε
        // τίτλο ή αναδόχους, ο εξυπηρετητής θα ζητούσε δικαίωμα διαχείρισης και θα
        // απέρριπτε μια ενέργεια που ο Πίνακας 1 του επιτρέπει.
        const changes = canManage ? { ...managedFields(), status: form.status } : { status: form.status };

        tasksService.updateTask(task.id, changes).then(onSaved, report);
    }

    function addNote(event) {
        event.preventDefault();
        if (note.trim() === '') return;

        setError('');
        setSaving(true);
        tasksService.addNote(task.id, note.trim()).then(
            () => {
                setNote('');
                onSaved();
            },
            report
        );
    }

    function remove() {
        setError('');
        setSaving(true);
        tasksService.deleteTask(task.id).then(onSaved, report);
    }

    // ⚠ **Η ΕΠΕΞΕΡΓΑΣΙΑ ΕΙΝΑΙ ΚΑΤΑΣΤΑΣΗ, ΟΧΙ Η ΠΡΟΕΠΙΛΟΓΗ.**
    //
    // Ο διάλογος άνοιγε με **έξι πεδία φόρμας** ακόμη και όταν ο χρήστης ήθελε
    // απλώς να διαβάσει τι είναι το καθήκον ή να προσθέσει σημείωση. Πεδία
    // εισόδου διαβάζονται ως «συμπλήρωσέ με»: η οθόνη ζητούσε ενέργεια που
    // κανείς δεν είχε ζητήσει, και έκρυβε το περιεχόμενο μέσα σε πλαίσια.
    //
    // **Στο νέο καθήκον η επεξεργασία είναι υποχρεωτικά ανοιχτή**: δεν υπάρχει
    // τίποτα να διαβαστεί, και μια λειτουργία ανάγνωσης με κενά πεδία θα ήταν
    // οθόνη που δεν κάνει τίποτα.
    //
    // Δύο δρόμοι για να ανοίξει, και οι δύο υπάρχουν επίτηδες: το **κουμπί**,
    // που είναι ο ανακαλύψιμος, και το **διπλό κλικ στην περιγραφή**, που είναι
    // ο γρήγορος. Ο δεύτερος δεν αντικαθιστά τον πρώτο — διπλό κλικ δεν
    // ανακαλύπτεται και δεν υπάρχει στο πληκτρολόγιο.
    const [isEditing, setEditing] = useState(isNew);
    const canEditFields = canManage && isEditing;

    function startEditing() {
        if (canManage) setEditing(true);
    }

    function showNotes() {
        setPane('notes');
    }

    function showActivity() {
        setPane('activity');
    }

    return (
        <Modal.Root isOpen onOpenChange={(open) => { if (!open) onClose(); }}>
            <Modal.Backdrop>
                <Modal.Container>
                    {/* **Δύο στήλες θέλουν πλάτος.** Το προηγούμενο ελάχιστο
                        (40vw) ήταν γραμμένο για μία στήλη· με δύο, η δεξιά
                        έπεφτε σε είκοσι χαρακτήρες ανά γραμμή και οι φυσαλίδες
                        γίνονταν σκάλα. Το `max-w` υπάρχει για το αντίθετο άκρο:
                        σε ευρεία οθόνη, γραμμή κειμένου εκατόν είκοσι
                        χαρακτήρων δεν διαβάζεται. */}
                    <Modal.Dialog className="w-full sm:min-w-[64vw] sm:max-w-5xl">
                        <Modal.Header>
                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                {/* ⚠ **Η ΠΡΟΘΕΣΜΙΑ ΠΑΝΩ ΑΠΟ ΤΟΝ ΤΙΤΛΟ, ΜΕ ΤΗΝ ΙΔΙΑ
                                    ΟΨΗ ΠΟΥ ΕΧΕΙ ΣΤΗΝ ΚΑΡΤΑ.** Ίδιο συστατικό, όχι
                                    ίδιο στιλ γραμμένο δεύτερη φορά: ο χρήστης
                                    έρχεται εδώ **από** την κάρτα, και η ίδια
                                    πληροφορία με άλλη μορφή διαβάζεται ως άλλη
                                    πληροφορία.

                                    Ήταν κείμενο σε μικρά γκρι γράμματα στο κάτω
                                    μέρος, χωρίς χρώμα — δηλαδή η **λήξη** μιας
                                    προθεσμίας, που είναι ολόκληρο το νόημα της
                                    πιλοτικής, δεν φαινόταν πουθενά στην οθόνη που
                                    ανοίγει ο ανάδοχος. */}
                                {!isNew && <DueBadge value={task.dueDate} inline />}

                                {/* ⚠ **Ο ΤΙΤΛΟΣ ΕΠΕΞΕΡΓΑΖΕΤΑΙ ΣΤΗ ΘΕΣΗ ΤΟΥ**, με το
                                    πρότυπο της κάρτας πόρου: ίδιο μέγεθος, ίδιο
                                    βάρος, χωρίς ετικέτα από πάνω. Ένα πεδίο με
                                    ετικέτα «Τίτλος» κάπου μέσα στη φόρμα ζητά από
                                    τον αναγνώστη να κάνει μόνος του την
                                    αντιστοίχιση με το όνομα που μόλις διάβασε. */}
                                {canEditFields ? (
                                    <TextField
                                        value={form.title}
                                        onChange={set('title')}
                                        aria-label={TASKS.taskTitle}
                                        isRequired
                                    >
                                        <Input className="text-xl font-semibold" placeholder={TASKS.taskTitlePlaceholder} />
                                    </TextField>
                                ) : (
                                    <Modal.Heading className="break-words">
                                        {isNew ? TASKS.createTitle : task.title}
                                    </Modal.Heading>
                                )}
                            </div>
                            <Modal.CloseTrigger />
                        </Modal.Header>

                        {/* ⚠ **ΟΙ ΔΥΟ ΣΤΗΛΕΣ ΕΙΝΑΙ ΜΙΑ ΣΕ ΣΤΕΝΗ ΟΘΟΝΗ**, και η
                            σειρά τους σε αυτή την περίπτωση δεν είναι αδιάφορη:
                            η επεξεργασία προηγείται της συζήτησης, γιατί εκείνη
                            είναι ο λόγος που άνοιξε ο διάλογος. Με `lg:` το
                            σπάσιμο γίνεται στο πλάτος όπου δύο στήλες κειμένου
                            χωρούν πράγματι — όχι στο `sm:`, όπου θα χωρούσαν
                            οριακά και θα διαβάζονταν και οι δύο άσχημα. */}
                        <Modal.Body className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                        <div className="flex flex-col gap-4">
                            {/* Η φόρμα ζει **μέσα** στο σώμα και ποτέ γύρω από κεφαλίδα,
                                σώμα και υποσέλιδο: τυλιγμένη γύρω τους σπάει την αλυσίδα
                                flex του διαλόγου, το σώμα παύει να κυλά και το υποσέλιδο
                                βγαίνει έξω από το ορατό παράθυρο. Το κουμπί υποβολής
                                δείχνει τη φόρμα με το `form`. */}
                            <form id="task-form" onSubmit={save} noValidate className="flex flex-col gap-4">
                                {/* ⚠ **Η ΚΑΤΑΣΤΑΣΗ ΜΕΤΑΚΟΜΙΣΕ ΣΤΟ ΥΠΟΣΕΛΙΔΟ,
                                    ΔΙΠΛΑ ΣΤΗΝ ΑΠΟΘΗΚΕΥΣΗ.** Ο προηγούμενος γύρος
                                    την ανέβασε **πάνω από τον τίτλο** με σωστό
                                    επιχείρημα —είναι ο λόγος που ανοίγει ο
                                    διάλογος— και η θέση εκείνη είχε δικό της
                                    τίμημα: η κατάσταση είναι **ενέργεια**, και
                                    καθόταν μέσα στην περιοχή που πλέον διαβάζεται.

                                    Το υποσέλιδο είναι όπου ζουν οι ενέργειες, και
                                    είναι **πάντοτε ορατό** χωρίς κύλιση. Ο ανάδοχος
                                    χωρίς δικαίωμα επεξεργασίας βρίσκει εκεί τη μόνη
                                    του ενέργεια, δίπλα στην αποθήκευση.

                                    Για όποιον **δεν** μπορεί να την αλλάξει μένει
                                    εδώ η **ετικέτα**: η πληροφορία δεν εξαρτάται από
                                    δικαίωμα, η ενέργεια εξαρτάται. */}
                                {!canProgress && !isNew && (
                                    <div className="flex flex-col gap-1">
                                        <TaskStatusChip status={task.status} className="self-start" />
                                        <p className="text-xs text-muted">{TASKS.onlyAssignees}</p>
                                    </div>
                                )}

                                {canEditFields ? (
                                    <>
                                        {/* Ο **τίτλος** δεν είναι εδώ: επεξεργάζεται
                                            στη θέση του, στην κεφαλίδα. */}
                                        <TextField value={form.description} onChange={set('description')}>
                                            <Label>{TASKS.description}</Label>
                                            <TextArea rows={3} />
                                        </TextField>

                                        <Select placeholder={SELECT_PLACEHOLDER}
                                            selectedKey={form.resource || 'none'}
                                            onSelectionChange={(key) =>
                                                set('resource')(String(key) === 'none' ? '' : String(key))}
                                        >
                                            <Label>{TASKS.resource}</Label>
                                            <Select.Trigger>
                                                <Select.Value />
                                                <Select.Indicator />
                                            </Select.Trigger>
                                            <Select.Popover>
                                                <ListBox>
                                                    <ListBoxItem id="none">{TASKS.noResource}</ListBoxItem>
                                                    {resources.map((resource) => (
                                                        <ListBoxItem key={resource.id} id={resource.id}>
                                                            {resource.title}
                                                        </ListBoxItem>
                                                    ))}
                                                </ListBox>
                                            </Select.Popover>
                                        </Select>
                                        <p className="-mt-2 text-xs text-muted">{TASKS.resourceHint}</p>

                                        {/* ⚠ **ΤΑ ΜΕΛΗ ΩΣ ΠΡΟΣΩΠΑ, ΜΕ ΕΙΚΟΝΑ.** Ο
                                            κατάλογος πλαισίων ελέγχου ήταν
                                            σωστός και **ασύμφωνος με την κάρτα**,
                                            όπου ο ίδιος άνθρωπος εμφανίζεται ως
                                            πρόσωπο: ο χρήστης έβλεπε δύο
                                            διαφορετικές αναπαραστάσεις της ίδιας
                                            σχέσης σε δύο οθόνες που ανοίγουν η
                                            μία την άλλη.

                                            **Το πλαίσιο ελέγχου δεν έφυγε** — ο
                                            μηχανισμός επιλογής παραμένει ο ίδιος
                                            και το `<fieldset>` με τη `<legend>`
                                            του κρατά την ομαδοποίηση για τον
                                            αναγνώστη οθόνης. Ό,τι άλλαξε είναι
                                            **τι δείχνει η ετικέτα**: εικόνα και
                                            όνομα αντί για όνομα σκέτο. */}
                                        <fieldset className="flex flex-col gap-2">
                                            <legend className="text-sm font-medium">{TASKS.assignees}</legend>
                                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                                                {members.map((member) => (
                                                    <Checkbox
                                                        key={member.id}
                                                        isSelected={form.assignees.includes(String(member.id))}
                                                        onChange={() => toggleAssignee(String(member.id))}
                                                    >
                                                        <Checkbox.Content>
                                                            <Checkbox.Control>
                                                                <Checkbox.Indicator />
                                                            </Checkbox.Control>
                                                            <UserBadge user={member} />
                                                        </Checkbox.Content>
                                                    </Checkbox>
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted">{TASKS.assigneesHint}</p>
                                        </fieldset>

                                        <TextField value={form.dueDate} onChange={set('dueDate')}>
                                            <Label>{TASKS.dueDate}</Label>
                                            <Input type="date" />
                                        </TextField>
                                    </>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {/* ⚠ **ΔΙΠΛΟ ΚΛΙΚ ΣΤΗΝ ΠΕΡΙΓΡΑΦΗ ΑΝΟΙΓΕΙ ΤΗΝ
                                            ΕΠΕΞΕΡΓΑΣΙΑ**, όπως στους γνωστούς πίνακες
                                            καρτών. Είναι **συντόμευση και όχι ο
                                            δρόμος**: το κουμπί στο υποσέλιδο κάνει το
                                            ίδιο και είναι ο ανακαλύψιμος — διπλό κλικ
                                            δεν το βρίσκει κανείς μόνος του, δεν
                                            υπάρχει στο πληκτρολόγιο, και στην αφή
                                            είναι αναξιόπιστο.

                                            Ο κανόνας είναι ο ίδιος με τη **μεταφορά**
                                            στον πίνακα Kanban (**D28**): ο γρήγορος
                                            τρόπος δεν επιτρέπεται να είναι ο μόνος.

                                            Το πλαίσιο υπάρχει **και όταν η περιγραφή
                                            λείπει**, με ρητό μήνυμα: αλλιώς δεν θα
                                            υπήρχε τίποτα να πατηθεί ακριβώς στην
                                            περίπτωση όπου κάποιος θέλει να γράψει. */}
                                        <div
                                            onDoubleClick={startEditing}
                                            className={[
                                                'rounded-xl border border-border bg-surface p-3 text-sm whitespace-pre-line',
                                                canManage ? 'cursor-text' : ''
                                            ].filter(Boolean).join(' ')}
                                            title={canManage ? TASKS.editHint : undefined}
                                        >
                                            {task.description || (
                                                <span className="text-muted">{TASKS.noDescription}</span>
                                            )}
                                        </div>

                                        {/* Ο συνδεδεμένος πόρος, όταν υπάρχει: είναι το
                                            πλαίσιο μέσα στο οποίο διαβάζεται ο τίτλος
                                            (**D41**), και έλειπε εντελώς από τη
                                            λειτουργία ανάγνωσης. */}
                                        {task.resource?.title && (
                                            <p className="text-xs text-muted">
                                                {`${TASKS.resource}: ${task.resource.title}`}
                                            </p>
                                        )}
                                        {/* Ο ίδιος τρόπος να δειχτεί ο ανάδοχος με
                                            την κάρτα και με την επεξεργασία: μία
                                            αναπαράσταση προσώπου σε όλη την οθόνη. */}
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted">{`${TASKS.assignees}:`}</span>
                                            {(task.assignees ?? []).length === 0 ? (
                                                <span className="text-xs text-muted">{TASKS.noAssignees}</span>
                                            ) : (
                                                <AvatarStack users={task.assignees} label={TASKS.assigneeOf} />
                                            )}
                                        </div>
                                        {/* Η **προθεσμία δεν επαναλαμβάνεται εδώ**:
                                            ζει στην κεφαλίδα, με χρώμα, στην ίδια
                                            μορφή που έχει και στην κάρτα. Η γραμμή
                                            που ήταν εδώ την έγραφε σε γκρι κείμενο
                                            με **άλλη** μορφή ημερομηνίας — δεύτερη
                                            διατύπωση της ίδιας πληροφορίας, που
                                            μάλιστα απέκλινε. */}
                                    </div>
                                )}

                            </form>

                            {/* Το σφάλμα **δίπλα στις ενέργειες**, στο τέλος του σώματος
                                και όχι στην κορυφή: το σώμα του διαλόγου κυλά, και ένα
                                μήνυμα στην κορυφή του μπορεί να είναι εκτός οθόνης τη
                                στιγμή που ο χρήστης πατά «Αποθήκευση». */}
                            {error && (
                                <Notice status="warning" title={TASKS.title}>
                                    {error}
                                </Notice>
                            )}

                            {/* ΜΛΑ-03: η επιβεβαίωση διαγραφής λέει **τι χάνεται**, πριν
                                γίνει. Μένει μέσα στον ίδιο διάλογο αντί να ανοίξει
                                δεύτερο: ένας εμφωλευμένος διάλογος έχει το ίδιο πρόβλημα
                                κλεισίματος με τον πρώτο, πολλαπλασιασμένο. */}
                            {isConfirmingDelete && (
                                <div className="flex flex-col gap-3 rounded-lg border border-danger p-3">
                                    <Notice status="danger" title={TASKS.removeTitle}>
                                        {`${TASKS.removeWarning} (${notes.length})`}
                                    </Notice>
                                    <div className="flex gap-2">
                                        <Button color="danger" onPress={remove} isPending={isSaving}>
                                            {TASKS.removeConfirm}
                                        </Button>
                                        <Button variant="outline" onPress={() => setConfirmingDelete(false)}>
                                            {TASKS.cancel}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── ΔΕΞΙΑ ΣΤΗΛΗ: η συζήτηση και το ιστορικό ───────────
                            Και τα δύο υπάρχουν **μόνο σε υπαρκτό καθήκον**: σε
                            νέο δεν υπάρχει τίποτα να δειχτεί, και δύο κενές
                            κάρτες δίπλα σε μια άδεια φόρμα διαβάζονται ως
                            «κάτι δεν φόρτωσε». */}
                        {!isNew && (
                            <div className="flex flex-col gap-4 lg:sticky lg:top-0">
                                {/* ⚠ **ΚΑΡΤΕΛΕΣ ΑΝΤΙ ΓΙΑ ΔΥΟ ΕΝΟΤΗΤΕΣ ΣΤΗ ΣΕΙΡΑ.**
                                    Οι σημειώσεις και το ιστορικό κάθονταν η μία
                                    κάτω από την άλλη, οπότε το ιστορικό ήταν
                                    πρακτικά αόρατο: σε καθήκον με πέντε σημειώσεις
                                    ξεκινούσε **κάτω από το ορατό παράθυρο**, και
                                    κανείς δεν κυλά μια στήλη που φαίνεται
                                    τελειωμένη.

                                    Είναι το **ίδιο συστατικό καρτέλας** με τον
                                    κατάλογο συλλογών και με το παράθυρο του πόρου:
                                    τρίτος καταναλωτής, καμία νέα υλοποίηση. */}
                                <div className="flex flex-wrap gap-2">
                                    <ViewTab
                                        icon="fa-regular fa-comments"
                                        isActive={pane === 'notes'}
                                        onPress={showNotes}
                                    >
                                        {`${TASKS.notes} (${notes.length})`}
                                    </ViewTab>
                                    <ViewTab
                                        icon="fa-solid fa-clock-rotate-left"
                                        isActive={pane === 'activity'}
                                        onPress={showActivity}
                                    >
                                        {TASKS.taskActivity}
                                    </ViewTab>
                                </div>

                                {/* ΛΑ-20: οι σημειώσεις προόδου. */}
                                <section className={pane === 'notes' ? 'flex flex-col gap-3' : 'hidden'}>
                                    {notes.length === 0 ? (
                                        <p className="text-sm text-muted">{TASKS.noNotes}</p>
                                    ) : (
                                        <ul className="flex flex-col gap-4">
                                            {notes.map((entry) => (
                                                <CommentBubble
                                                    key={entry._id ?? entry.createdAt}
                                                    author={entry.author}
                                                    text={entry.text}
                                                    createdAt={entry.createdAt}
                                                />
                                            ))}
                                        </ul>
                                    )}

                                    {canProgress && (
                                        <form onSubmit={addNote} className="flex flex-col gap-2">
                                            {/* D44: η σημείωση προόδου είναι
                                                πεδίο **συνομιλίας**. Τα μέλη τα
                                                έχει ήδη ο διάλογος από τη συλλογή
                                                του, οπότε δεν γίνεται κανένα
                                                επιπλέον αίτημα. */}
                                            <MentionTextArea
                                                value={note}
                                                onChange={setNote}
                                                members={members}
                                                label={TASKS.addNote}
                                                isLabelHidden
                                                rows={2}
                                                placeholder={TASKS.notePlaceholder}
                                            />
                                            <p className="text-xs text-muted">{MENTIONS.hint}</p>
                                            <Button
                                                type="submit"
                                                size="sm"
                                                variant="outline"
                                                isDisabled={note.trim() === '' || isSaving}
                                                className="self-start"
                                            >
                                                {TASKS.addNote}
                                            </Button>
                                        </form>
                                    )}
                                </section>

                                {/* ⚠ **ΤΟ ΕΥΡΟΣ ΕΙΝΑΙ ΤΟ ΚΡΙΣΙΜΟ ΣΗΜΕΙΟ ΕΔΩ.**
                                    Το ιστορικό έρχεται από την **υπάρχουσα**
                                    διαδρομή της συλλογής, με τον **υπάρχοντα**
                                    έλεγχο ορατότητας — τον ίδιο `loadCollection`
                                    και `authorize` που φυλάει τη ροή της
                                    συλλογής. Το `task` δεν είναι δεύτερο φίλτρο
                                    αλλά δεύτερη **πόρτα** του ίδιου: δένει το
                                    `objectId` του γεγονότος, όπως ακριβώς το
                                    `resource` της ΛΑ-23.

                                    Καμία νέα διατύπωση κανόνα, και καμία νέα
                                    διαδρομή: αν το ιστορικό ενός καθηκόντος
                                    χρειαζόταν δικό του σημείο ανάγνωσης, θα
                                    ήταν δεύτερη ευκαιρία στον κανόνα ορατότητας
                                    να αποκλίνει. */}
                                {/* **Αποδίδεται μόνο όταν είναι η ενεργή καρτέλα**,
                                    και όχι κρυμμένο με κλάση: το ιστορικό κάνει
                                    **δικό του αίτημα**, και ένα κρυφό συστατικό που
                                    ρωτά τον εξυπηρετητή είναι κόστος για κάτι που
                                    κανείς δεν κοιτά. Η αντίθεση με τις σημειώσεις
                                    από πάνω είναι σκόπιμη: εκείνες ταξιδεύουν ήδη
                                    μαζί με το καθήκον και δεν κοστίζουν τίποτα. */}
                                {pane === 'activity' && (
                                    <CollectionActivity
                                        collectionId={collectionId}
                                        task={task.id}
                                        title={TASKS.taskActivityTitle}
                                        emptyMessage={TASKS.taskActivityEmpty}
                                    />
                                )}
                            </div>
                        )}
                        </Modal.Body>

                        <Modal.Footer className="flex flex-wrap items-center gap-2">
                            {/* ⚠ **ΤΟ «ΑΠΟΘΗΚΕΥΣΗ» ΥΠΑΡΧΕΙ ΜΟΝΟ ΟΤΑΝ ΥΠΑΡΧΕΙ ΚΑΤΙ ΝΑ
                                ΑΠΟΘΗΚΕΥΤΕΙ.** Σε λειτουργία ανάγνωσης δεν υπάρχει
                                πρόχειρο: κουμπί αποθήκευσης εκεί υπόσχεται ενέργεια
                                που δεν έχει αντικείμενο, και ο χρήστης δεν ξέρει αν
                                το πάτησε «σωστά». */}
                            {canEditFields && (
                                <Button type="submit" form="task-form" isPending={isSaving}>
                                    {isSaving ? TASKS.saving : TASKS.save}
                                </Button>
                            )}

                            {/* ⚠ **Η ΚΑΤΑΣΤΑΣΗ ΔΙΠΛΑ ΣΤΗΝ ΑΠΟΘΗΚΕΥΣΗ**, όχι μέσα στη
                                φόρμα. Είναι η ενέργεια που κάνει ο ανάδοχος, και ο
                                ανάδοχος συχνά **δεν** έχει δικαίωμα επεξεργασίας
                                πεδίων: για εκείνον το υποσέλιδο είναι η μόνη
                                περιοχή της οθόνης που τον αφορά.

                                Δεν αποδίδεται σε **νέο** καθήκον: ένα καθήκον που
                                δεν υπάρχει ακόμη δεν έχει κατάσταση να αλλάξει, και
                                το πεδίο της φόρμας το ορίζει ήδη ως «προς
                                εκτέλεση». */}
                            {!isNew && canProgress && (
                                <Select placeholder={SELECT_PLACEHOLDER}
                                    selectedKey={form.status}
                                    onSelectionChange={handleStatusChange}
                                    aria-label={TASKS.statusLabel}
                                    className="w-auto min-w-0"
                                >
                                    <Select.Trigger className="flex items-center gap-2">
                                        <i
                                            className={TASK_STATUS_APPEARANCE[form.status]?.icon ?? TASK_STATUS_APPEARANCE.todo.icon}
                                            aria-hidden="true"
                                        />
                                        <Select.Value />
                                        <Select.Indicator />
                                    </Select.Trigger>
                                    <Select.Popover>
                                        <ListBox>
                                            {Object.entries(TASKS.status).map(([key, label]) => (
                                                <ListBoxItem key={key} id={key}>{label}</ListBoxItem>
                                            ))}
                                        </ListBox>
                                    </Select.Popover>
                                </Select>
                            )}

                            {/* Ο **ανακαλύψιμος** δρόμος προς την επεξεργασία. Το
                                διπλό κλικ στην περιγραφή κάνει το ίδιο και είναι η
                                συντόμευση· αυτό εδώ είναι ο δρόμος. */}
                            {!isNew && canManage && !isEditing && (
                                <Button variant="outline" onPress={startEditing}>
                                    <i className="fa-solid fa-pen" aria-hidden="true" />
                                    {TASKS.edit}
                                </Button>
                            )}

                            {/* ⚠ **ΜΟΝΟ «ΑΚΥΡΩΣΗ», ΚΑΙ ΜΟΝΟ ΟΤΑΝ ΥΠΑΡΧΕΙ ΠΡΟΧΕΙΡΟ.**
                                Σε λειτουργία ανάγνωσης το κουμπί έλεγε «Κλείσιμο»
                                και έκανε ό,τι ακριβώς και το **×** της κεφαλίδας
                                και το Esc: τρεις τρόποι για την ίδια ενέργεια, ο
                                ένας από αυτούς στη γραμμή που περιέχει τις
                                **πραγματικές** ενέργειες του καθηκόντος.

                                Η ακύρωση **μένει**, γιατί δεν είναι το ίδιο πράγμα:
                                σημαίνει «πέταξε ό,τι πληκτρολόγησα», και ο χρήστης
                                πρέπει να τη βρίσκει δίπλα στην αποθήκευση. */}
                            {canEditFields && (
                                <Button variant="outline" onPress={onClose}>{TASKS.cancel}</Button>
                            )}

                            {!isNew && canManage && !isConfirmingDelete && (
                                <Button
                                    variant="outline"
                                    color="danger"
                                    onPress={() => setConfirmingDelete(true)}
                                    className="ms-auto"
                                >
                                    {TASKS.remove}
                                </Button>
                            )}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal.Root>
    );
}
